/**
 * Guía del builder (PR-6 builder reorg — D10; Plan J J2 adds ②, J3 adds
 * ③④⑤, J4 adds ⑥).
 *
 * Nine static, collapsible chapters for team onboarding:
 *   ① Data & views (S3 → loader → DuckDB → views → elements)
 *   ② Dimensions (column / mapping / computed → enrichment → filters
 *      and access scopes)
 *   ③ Scopes (rules → predicate → one file per project → the reader)
 *   ④ Groups (the only place access is written)
 *   ⑤ People (the union of somebody's groups)
 *   ⑥ Verify before granting (build → count → look → mail → trail)
 *   ⑦ Auto-refresh & reconciliation
 *   ⑧ Publishing
 *   ⑨ The AI and its improvement loop
 *
 * The three J3 chapters sit at 3/4/5 for the same reason J2's sits at
 * 2: the order teaches the pipeline. A scope cuts by a dimension, a
 * group carries a scope, a person is the union of their groups — so
 * access follows the dimension it depends on and precedes the operations
 * chapters, which is also the order of the operator runbook's §3
 * checklist (dimensión → ámbito → grupo → persona). Inserting AFTER
 * index 1 is also what keeps `guide-page.test.tsx`'s pin on
 * `CHAPTERS[1]` and the runbook's two references to "capítulo 2
 * «Dimensiones»" true with no edit.
 *
 * ⑥ CLOSES THAT ARC AND MUST BE LAST IN IT. `verify` is the walk an
 * analyst does before a real person is bound to a restricted scope, so
 * it is the only chapter whose steps span all four screens the previous
 * three describe plus the portal, the chat and the mail. It reads as
 * nonsense before Personas (it checks a person) and as an afterthought
 * behind Auto-refresh (it is a gate, not an operation), so index 5 —
 * after `people`, before `refresh` — is the one position that is an
 * argument. It also keeps every pin in `guide-page.test.tsx` true:
 * that file names `CHAPTERS[1..4]` by index and nothing beyond.
 *
 * Each chapter: an SVG flow diagram (brand tokens: navy #211f4b,
 * primary #4D4A9D, accent #E5197D) + numbered steps + a "details that
 * matter" callout. All copy lives in i18n (EN/ES) under `guide.*`.
 * Purely static — no API calls.
 *
 * EVERY KEY ON THIS PAGE IS BUILT BY TEMPLATE — `guide.${ch.id}.step${i}`
 * and friends. The vitest `i18n-keys.test.ts` only matches literal
 * `t("…")`, so it is structurally blind to all ~102 of them, and this
 * app's i18next carries `fallbackLng: "en"`, so a missing ES key renders
 * ENGLISH at a Spanish operator instead of failing. Nothing in the app
 * or the browser can see the omission. `.github/scripts/check_guide_keys.py`
 * is what does: it reads `CHAPTERS` and `FLOW_NODES` below and demands
 * the exact key set they imply, in BOTH languages, with the Spanish not
 * a copy of the English.
 */

import { Card, CardBody } from "@omnitok/ui";
import { brand } from "@pervasivemind/bi-components/brand";
import { Disclosure, PageHeader } from "@pervasivemind/admin-ui";
import {
  Bot,
  ClipboardCheck,
  Columns3,
  Database,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  UserRound,
  Users,
} from "lucide-react";

import { useT } from "@/i18n";

/**
 * Exported for `__tests__/guide-page.test.tsx`, which renders the real
 * component rather than asserting about it — the only way the "a chapter
 * missing from FLOW_NODES draws nothing" claim below is a measurement.
 */
export const CHAPTERS = [
  { id: "data", icon: Database, steps: 5, details: 3 },
  // Chapter TWO on purpose: the enrichment runs inside the reload the
  // previous chapter just described, before its first view. Third or
  // fifth would teach the pipeline out of order. The on-screen numbers
  // are `idx + 1`, so this renumbers refresh/publish/ai to 3/4/5 —
  // nothing cross-references a chapter by number (grepped docs/ and
  // .claude/); prefer the id if anything ever does.
  //
  // 6 steps / 4 details is a deliberate departure from the 5/3 house
  // shape: the six steps are the six things an analyst actually does,
  // and the fourth detail is the `usable_as` trap, which the operator
  // runbook already calls "el que muerde" because the row looks
  // complete in any SELECT while every scope silently resolves
  // "no aplica".
  { id: "dimensions", icon: Columns3, steps: 6, details: 4 },
  // Plan J J3 — the three access chapters, inserted here and not
  // appended. See the file docstring for why the position is the
  // argument. FIELD ORDER IS LOAD-BEARING: `check_guide_keys.chapters()`
  // pins `id, icon, steps, details` with a strict pattern and then
  // re-reads every `id:` with a laxer one, so an entry written in any
  // other order raises rather than going invisible — that bypass shipped
  // once and the gate's docstring carries the measurement.
  //
  // 6/4 for scopes matches Dimensiones and for the same reason: the
  // fourth detail is the one that stops somebody adding a fallback to
  // the project's file. Grupos is 5/3 (the house shape) and Personas
  // 6/3 — its sixth step is the "next request, not next login" fact,
  // which is the first thing an analyst asks and the last thing they
  // guess right.
  { id: "scopes", icon: ShieldCheck, steps: 6, details: 4 },
  { id: "groups", icon: Users, steps: 5, details: 3 },
  { id: "people", icon: UserRound, steps: 6, details: 3 },
  // Plan J J4 — the walk, and the only chapter with SEVEN steps.
  // The count is not decoration: the walk has seven acts and the
  // chapter is read while somebody is waiting for access, so folding
  // two of them into one paragraph ("look, then mail") is precisely
  // how the step that gets skipped becomes the one that mattered.
  // Six of the seven are a different screen or a different app.
  { id: "verify", icon: ClipboardCheck, steps: 7, details: 4 },
  { id: "refresh", icon: RefreshCw, steps: 5, details: 3 },
  { id: "publish", icon: UploadCloud, steps: 4, details: 3 },
  { id: "ai", icon: Bot, steps: 5, details: 3 },
] as const;

/**
 * Flow nodes per chapter — labels resolved via `guide.{ch}.node{i}`.
 *
 * This is a SECOND source of truth for the same chapter list and it
 * fails SILENTLY. `tsconfig.app.json` sets `strict` but not
 * `noUncheckedIndexedAccess`, so `FLOW_NODES[missingId]` types as
 * `number` and evaluates to `undefined`: `Array.from({length: undefined})`
 * yields zero boxes and the width arithmetic yields `NaN`, so the
 * chapter renders `<svg viewBox="0 0 NaN 64">` with nothing inside it
 * and `tsc` stays green. (The height is `BOX_H + 8`, a constant, so only
 * the width goes NaN — the plan predicted `NaN NaN` and was wrong. The
 * browser drops a malformed viewBox either way and the `w-full` <svg>
 * keeps its box, so what a reader sees is a blank gap, not an error.)
 * Measured in `__tests__/guide-page.test.tsx`, not assumed. Assertion A
 * of `check_guide_keys.py` exists for exactly this.
 */
export const FLOW_NODES: Record<string, number> = {
  data: 5,
  dimensions: 5,
  scopes: 5,
  groups: 5,
  people: 5,
  verify: 5,
  refresh: 5,
  publish: 4,
  ai: 5,
};

export function GuidePage(): JSX.Element {
  const { t } = useT();

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
      {/* A reading page keeps its narrower column (max-w-4xl); the head is
          the operator-page head every other builder screen uses. */}
      <PageHeader title={t("guide.title")} subtitle={t("guide.subtitle")} />

      <div className="space-y-4">
        {CHAPTERS.map((ch, idx) => {
          const Icon = ch.icon;
          return (
            <Disclosure
              key={ch.id}
              defaultOpen={idx === 0}
              // The old chevron was a `›` in `neutral-300` on the right:
              // the audit's note was that a closed chapter did not read
              // as openable at all. The shared disclosure puts a
              // `neutral-500` chevron on the left, where every other
              // one in the app is.
              summary={
                <span className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-neutral-900">
                      {idx + 1}. {t(`guide.${ch.id}.title`)}
                    </span>
                    <span className="block text-xs font-normal text-neutral-500">
                      {t(`guide.${ch.id}.intro`)}
                    </span>
                  </span>
                </span>
              }
              summaryClassName="px-5 py-4"
            >
              <div className="px-2 py-2">
                <FlowDiagram chapterId={ch.id} />

                <ol className="mt-4 space-y-2">
                  {Array.from({ length: ch.steps }, (_, i) => (
                    <li key={i} className="flex gap-2.5 text-sm">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
                        {i + 1}
                      </span>
                      <span className="text-neutral-700">
                        {t(`guide.${ch.id}.step${i + 1}`)}
                      </span>
                    </li>
                  ))}
                </ol>

                <Card
                  variant="bordered"
                  className="mt-4 border-accent/20 bg-accent/[0.03]"
                >
                  <CardBody className="py-3">
                    <p className="text-xs font-semibold text-accent">
                      {t("guide.detailsTitle")}
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {Array.from({ length: ch.details }, (_, i) => (
                        <li
                          key={i}
                          className="flex gap-2 text-xs text-neutral-600"
                        >
                          <span className="text-accent">•</span>
                          {t(`guide.${ch.id}.detail${i + 1}`)}
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              </div>
            </Disclosure>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Horizontal flow diagram: N rounded boxes joined by arrows. Node
 * labels (+ optional sublabel) come from i18n — `guide.{ch}.node{i}` /
 * `guide.{ch}.node{i}sub`. Pure SVG, brand palette, scales via
 * viewBox so it stays readable on mobile.
 */
export function FlowDiagram({
  chapterId,
}: {
  chapterId: string;
}): JSX.Element {
  const { t } = useT();
  const count = FLOW_NODES[chapterId];
  const BOX_W = 150;
  const BOX_H = 56;
  const GAP = 34;
  const width = count * BOX_W + (count - 1) * GAP + 8;
  const height = BOX_H + 8;

  // Los dos pasos claros bajaron un escalón porque el texto que va
  // ENCIMA es blanco y no llegaba: sobre #6f6cc4 el blanco pleno daba
  // 4.55 y sobre #E5197D 4.41 — y el subtítulo, a 75 % de opacidad,
  // 3.32 y 2.96. Siguen siendo el violeta y el magenta de la marca,
  // un tono más hondos: ahora 5.70 y 5.90 con blanco pleno.
  // Kit tokens (look v3 Fase 1): navy, violet, violet light, violet,
  // accent dark; the last node is the accent. White text on each is
  // >= 4.67:1.
  const palette = [brand.ink, brand.primary.DEFAULT, brand.primary.light, brand.primary.DEFAULT, brand.accent.dark];

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full min-w-[560px]"
        role="img"
        aria-label={t(`guide.${chapterId}.title`)}
      >
        {Array.from({ length: count }, (_, i) => {
          const x = 4 + i * (BOX_W + GAP);
          // El último recuadro es el de acento. Tomaba su color de un
          // literal en vez de la paleta, así que cuando la paleta bajó
          // un tono para que el blanco llegara a 4.5:1, este se quedó
          // con el viejo #E5197D y siguió midiendo 4.41 el título y
          // 3.77 el subtítulo. Ahora sale del mismo array: dos lugares
          // con el mismo color es cómo se separan.
          const fill =
            i === count - 1
              ? palette[palette.length - 1]
              : palette[i % palette.length];
          const label = t(`guide.${chapterId}.node${i + 1}`);
          const sub = t(`guide.${chapterId}.node${i + 1}sub`);
          return (
            <g key={i}>
              <rect
                x={x}
                y={4}
                width={BOX_W}
                height={BOX_H}
                rx={10}
                fill={fill}
              />
              <text
                x={x + BOX_W / 2}
                y={4 + BOX_H / 2 - (sub ? 6 : -1)}
                textAnchor="middle"
                fill="white"
                fontSize={13}
                fontWeight={700}
                fontFamily="'Nunito Sans', sans-serif"
              >
                {label}
              </text>
              {sub && (
                <text
                  x={x + BOX_W / 2}
                  y={4 + BOX_H / 2 + 12}
                  textAnchor="middle"
                  // 10 px y 75 % era el peor texto de la consola: por
                  // debajo del piso de 12 px y por debajo de 4.5:1 en
                  // los dos recuadros claros. La jerarquía la sigue
                  // dando el peso, no la transparencia.
                  fill="rgba(255,255,255,0.9)"
                  fontSize={12}
                  fontFamily="'Nunito Sans', sans-serif"
                >
                  {sub}
                </text>
              )}
              {i < count - 1 && (
                <g>
                  <line
                    x1={x + BOX_W + 4}
                    y1={4 + BOX_H / 2}
                    x2={x + BOX_W + GAP - 10}
                    y2={4 + BOX_H / 2}
                    stroke={brand.semantic.infoLight}
                    strokeWidth={2}
                  />
                  <polygon
                    points={`${x + BOX_W + GAP - 10},${BOX_H / 2 - 1} ${x + BOX_W + GAP - 10},${BOX_H / 2 + 9} ${x + BOX_W + GAP - 2},${4 + BOX_H / 2}`}
                    fill={brand.semantic.infoLight}
                  />
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
